// src/components/SearchBar.jsx
import React, { useState } from 'react';
import { SYMPTOMS } from '../data/indications';

const SearchBar = ({ query, setQuery, filters, onFilterChange, onSearch }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const selectedSymptoms = filters.symptoms || [];

  const toggleSymptom = (id) => {
    onFilterChange(
      'symptoms',
      selectedSymptoms.includes(id)
        ? selectedSymptoms.filter(s => s !== id)
        : [...selectedSymptoms, id]
    );
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    onSearch();
  };
  
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // If query is at least 2 characters, suggest some common medicines
    if (value.length >= 2) {
      // These are example suggestions - in a real app you might fetch these from an API
      const commonMedicines = [
        'Vyvanse', 'Ritalin', 'Concerta', 'Adderall', 'Lamictal', 'Lexapro',
        'Zoloft', 'Prozac', 'Lipitor', 'Crestor', 'Advil', 'Tylenol',
        'Zyrtec', 'Claritin', 'Ambien', 'Xanax', 'Ativan', 'Viagra'
      ];
      
      // Filter to show only suggestions that match the query
      const matchedSuggestions = commonMedicines.filter(med => 
        med.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5); // Show max 5 suggestions
      
      setSuggestions(matchedSuggestions);
    } else {
      setSuggestions([]);
    }
  };
  
  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    setSuggestions([]);
    onSearch();
  };
  
  return (
    <div className="mb-6 bg-white p-4 rounded-lg shadow-md">
      <form onSubmit={handleSearch}>
        <div className="flex flex-wrap items-center gap-2">
          {/* Search input */}
          <div className="flex-grow relative">
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={handleQueryChange}
                placeholder="Search medicines by name, active ingredient, or NAPPI code"
                className="w-full py-2 px-4 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <i className="fas fa-search text-gray-400"></i>
              </span>
            </div>
            
            {/* Suggestions dropdown */}
            {suggestions.length > 0 && (
              <div className="absolute z-10 w-full bg-white mt-1 border border-gray-300 rounded-lg shadow-lg">
                <ul>
                  {suggestions.map((suggestion, index) => (
                    <li 
                      key={index}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {/* Search button */}
          <button 
            type="submit" 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-shrink-0"
          >
            Search
          </button>
          
          {/* Advanced toggle */}
          <button 
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 flex-shrink-0"
          >
            <i className={`fas fa-cog mr-1`}></i>
            {showAdvanced ? 'Hide Options' : 'Show Options'}
          </button>
        </div>
        
        {/* Advanced options */}
        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            {/* Symptom filter */}
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">
                  Filter by symptoms
                  {selectedSymptoms.length > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                      {selectedSymptoms.length} selected
                    </span>
                  )}
                </h3>
                {selectedSymptoms.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onFilterChange('symptoms', [])}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Clear symptoms
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Shows medicines commonly used for the selected symptoms. You can also leave the search box
                empty and search by symptoms alone. General reference only — not medical advice.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {SYMPTOMS.map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleSymptom(id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      selectedSymptoms.includes(id)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-700">Filter Options</h3>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.controlledOnly}
                    onChange={(e) => onFilterChange('controlledOnly', e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Controlled substances only</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.approvedOnly}
                    onChange={(e) => onFilterChange('approvedOnly', e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Approved medicines only</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.availableOnly}
                    onChange={(e) => onFilterChange('availableOnly', e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Available medicines only</span>
                </label>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-700">Data Sources</h3>
                
                {/* Data source options */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.useMpr !== false}
                    onChange={(e) => onFilterChange('useMpr', e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">MPR (Medicine Prices)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.useSahpra !== false}
                    onChange={(e) => onFilterChange('useSahpra', e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">SAHPRA (Regulatory)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.useDatabase !== false}
                    onChange={(e) => onFilterChange('useDatabase', e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Local Database (Cache)</span>
                </label>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-700">Cache Settings</h3>
                
                {/* Force fresh data option */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.forceFresh}
                    onChange={(e) => onFilterChange('forceFresh', e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Force fresh data from APIs</span>
                  <span className="ml-1 text-xs text-gray-500 cursor-help" title="Skips cache and fetches directly from external APIs">
                    <i className="fas fa-question-circle"></i>
                  </span>
                </label>
                
                {/* Cache expiry slider */}
                <div>
                  <label className="block text-sm text-gray-700">
                    Cache Expiry (hours): {filters.cacheExpiry || 24}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="72"
                    step="1"
                    value={filters.cacheExpiry || 24}
                    onChange={(e) => onFilterChange('cacheExpiry', parseInt(e.target.value))}
                    className="w-full h-2 bg-blue-100 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>1h</span>
                    <span>24h</span>
                    <span>72h</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default SearchBar;